import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  Scale, Loader2, Plus, Pencil, Trash2,
  Globe,
} from 'lucide-react';
import { StatutoryLeaveTemplateView, StatutoryLeaveTemplateInput, LeaveTypeView } from '../../../models/attendance';
import { CountryView } from '../../../models/general';
import * as LeavesApi from '../../../api/Attendance/leaves';
import { getCountries } from '../../../api/General/Country';
import { extractApiError } from '../../../utils/errors';
import { PageShell } from '../../../components/leaves/PageShell';
import Button from '../../../components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from '../../../components/ui/Dialog';

interface StatutoryLeaveTemplatesProps {
  userId: string;
  company: { id: number };
  role: string;
}

const emptyForm: StatutoryLeaveTemplateInput = {
  countryId: 0,
  leaveTypeName: '',
  minDaysPerYear: 0,
  carryForwardAllowed: false,
  maxCarryForwardDays: null,
  probationMonths: 0,
  notes: null,
};

const StatutoryLeaveTemplates: React.FC<StatutoryLeaveTemplatesProps> = ({ role }) => {
  const { t } = useTranslation(['leaves', 'common']);
  const isHr = ['Hr', 'Admin', 'Chief'].includes(role);

  const [templates, setTemplates] = useState<StatutoryLeaveTemplateView[]>([]);
  const [countries, setCountries] = useState<CountryView[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeView[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const [editModal, setEditModal] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<StatutoryLeaveTemplateInput>({ ...emptyForm });

  const [filterCountry, setFilterCountry] = useState<number>(0);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [templatesRes, countriesRes, leaveTypesRes] = await Promise.all([
        LeavesApi.getAllStatutoryTemplates(),
        getCountries(),
        LeavesApi.getCompanyLeaveTypes(),
      ]);
      if (templatesRes.errors.length === 0) setTemplates(templatesRes.payload);
      if (countriesRes.errors.length === 0) setCountries(countriesRes.payload);
      if (leaveTypesRes.errors.length === 0) setLeaveTypes(leaveTypesRes.payload);
    } catch {
      showToast(t('leaves:statutoryTemplates.loadError'), 'error');
    } finally {
      setLoading(false);
    }
  }, [t, showToast]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditId(null);
    setForm({ ...emptyForm });
    setEditModal(true);
  };

  const openEdit = (template: StatutoryLeaveTemplateView) => {
    setEditId(template.id);
    setForm({
      countryId: template.countryId,
      leaveTypeName: template.leaveTypeName,
      minDaysPerYear: template.minDaysPerYear,
      carryForwardAllowed: template.carryForwardAllowed,
      maxCarryForwardDays: template.maxCarryForwardDays,
      probationMonths: template.probationMonths,
      notes: template.notes,
    });
    setEditModal(true);
  };

  const handleSave = async () => {
    if (!form.countryId || !form.leaveTypeName || form.minDaysPerYear <= 0) {
      showToast(t('leaves:statutoryTemplates.validationError'), 'error');
      return;
    }
    setActionLoading('save');
    try {
      const res = editId
        ? await LeavesApi.updateStatutoryTemplate(editId, form)
        : await LeavesApi.createStatutoryTemplate(form);
      if (res.errors.length === 0) {
        showToast(t(editId ? 'leaves:statutoryTemplates.updated' : 'leaves:statutoryTemplates.created'), 'success');
        setEditModal(false);
        await loadData();
      } else {
        showToast(res.errors[0].message, 'error');
      }
    } catch (err) {
      showToast(extractApiError(err, t('common:Error')), 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id: number) => {
    setActionLoading(`delete-${id}`);
    try {
      const res = await LeavesApi.deleteStatutoryTemplate(id);
      if (res.errors.length === 0) {
        showToast(t('leaves:statutoryTemplates.deleted'), 'success');
        await loadData();
      } else {
        showToast(res.errors[0].message, 'error');
      }
    } catch (err) {
      showToast(extractApiError(err, t('common:Error')), 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredTemplates = filterCountry
    ? templates.filter(t => t.countryId === filterCountry)
    : templates;

  const groupedByCountry = filteredTemplates.reduce((acc, tmpl) => {
    const key = tmpl.countryId;
    if (!acc[key]) acc[key] = [];
    acc[key].push(tmpl);
    return acc;
  }, {} as Record<number, StatutoryLeaveTemplateView[]>);

  const getCountryLabel = (countryId: number) => {
    const tmpl = templates.find(t => t.countryId === countryId);
    return tmpl?.countryName || countries.find(c => c.id === countryId)?.nicename || String(countryId);
  };

  const getCountryIso = (countryId: number) => {
    const tmpl = templates.find(t => t.countryId === countryId);
    return tmpl?.countryIso || countries.find(c => c.id === countryId)?.iso || '';
  };

  const uniqueCountryIds = useMemo(
    () => [...new Set(templates.map(t => t.countryId))],
    [templates],
  );

  const stats = useMemo(() => [
    { label: t('leaves:statutoryTemplates.totalTemplates', 'Templates'), value: templates.length },
    { label: t('leaves:statutoryTemplates.countries', 'Countries'), value: uniqueCountryIds.length },
  ], [templates, uniqueCountryIds, t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="statutory-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <PageShell
      title={t('leaves:statutoryTemplates.title')}
      description={t('leaves:statutoryTemplates.description', 'Country-level statutory leave compliance templates')}
      icon={Scale}
      accentColor="emerald"
      maxWidth="6xl"
      stats={stats}
      headerActions={isHr ? (
        <Button
          variant="outline"
          onClick={openCreate}
          data-testid="create-template-btn"
          iconName="Plus"
        >
          {t('leaves:statutoryTemplates.addTemplate')}
        </Button>
      ) : undefined}
    >
      <div className="space-y-6" data-testid="statutory-templates-page">
      {/* Country filter */}
      <div className="flex items-center gap-3">
        <Globe className="w-5 h-5 text-muted-foreground" />
        <select
          value={filterCountry}
          onChange={e => setFilterCountry(Number(e.target.value))}
          className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
          data-testid="country-filter"
        >
          <option value={0}>{t('leaves:statutoryTemplates.allCountries')}</option>
          {uniqueCountryIds.sort((a, b) => getCountryLabel(a).localeCompare(getCountryLabel(b))).map(cId => (
            <option key={cId} value={cId}>{getCountryLabel(cId)}</option>
          ))}
        </select>
      </div>

      {/* Templates by country */}
      {Object.keys(groupedByCountry).length === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="no-templates">
          <Scale className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
          <p>{t('leaves:statutoryTemplates.noTemplates')}</p>
        </div>
      ) : (
        Object.entries(groupedByCountry)
          .sort(([a], [b]) => getCountryLabel(Number(a)).localeCompare(getCountryLabel(Number(b))))
          .map(([countryIdStr, tmplList]) => {
            const cId = Number(countryIdStr);
            return (
            <div key={cId} className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
              <div className="bg-muted px-5 py-3 border-b border-border flex items-center gap-2">
                <Globe className="w-4 h-4 text-primary" />
                <h2 className="font-semibold text-foreground">{getCountryLabel(cId)}</h2>
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{getCountryIso(cId)}</span>
              </div>
              <div className="divide-y divide-border">
                {tmplList.map(tmpl => (
                  <div key={tmpl.id} className="px-5 py-4 flex items-center justify-between hover:bg-muted transition-colors" data-testid={`template-row-${tmpl.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-foreground">{tmpl.leaveTypeName}</span>
                        <span className="text-sm bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                          {tmpl.minDaysPerYear} {t('leaves:statutoryTemplates.daysYear')}
                        </span>
                        {tmpl.carryForwardAllowed && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                            {t('leaves:statutoryTemplates.carryForward')}
                            {tmpl.maxCarryForwardDays != null && ` (${tmpl.maxCarryForwardDays}d)`}
                          </span>
                        )}
                        {tmpl.probationMonths > 0 && (
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                            {tmpl.probationMonths} {t('leaves:statutoryTemplates.monthsProbation')}
                          </span>
                        )}
                      </div>
                      {tmpl.notes && (
                        <p className="text-sm text-muted-foreground mt-1 truncate">{tmpl.notes}</p>
                      )}
                    </div>
                    {isHr && (
                      <div className="flex items-center gap-2 ms-4">
                        <button
                          onClick={() => openEdit(tmpl)}
                          className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                          data-testid={`edit-template-${tmpl.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(tmpl.id)}
                          disabled={actionLoading === `delete-${tmpl.id}`}
                          className="p-2 rounded-lg text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                          data-testid={`delete-template-${tmpl.id}`}
                        >
                          {actionLoading === `delete-${tmpl.id}` ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            );
          })
      )}

      {/* Create/Edit Modal */}
      <Dialog open={editModal} onOpenChange={setEditModal}>
        <DialogContent size="lg" data-testid="edit-template-modal">
          <DialogHeader>
            <DialogTitle>
              {editId ? t('leaves:statutoryTemplates.editTemplate') : t('leaves:statutoryTemplates.addTemplate')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t('leaves:statutoryTemplates.country')}</label>
              <select
                value={form.countryId}
                onChange={e => setForm({ ...form, countryId: Number(e.target.value) })}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                data-testid="form-country"
              >
                <option value={0}>{t('leaves:statutoryTemplates.selectCountry')}</option>
                {countries.map(c => (
                  <option key={c.id} value={c.id}>{c.nicename} ({c.iso})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t('leaves:statutoryTemplates.leaveTypeName')}</label>
              <select
                value={form.leaveTypeName}
                onChange={e => setForm({ ...form, leaveTypeName: e.target.value })}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                data-testid="form-leave-type-name"
              >
                <option value="">{t('leaves:statutoryTemplates.selectLeaveType')}</option>
                {leaveTypes.filter(lt => lt.isActive).map(lt => (
                  <option key={lt.id} value={lt.name}>{lt.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t('leaves:statutoryTemplates.minDays')}</label>
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  value={form.minDaysPerYear}
                  onChange={e => setForm({ ...form, minDaysPerYear: parseFloat(e.target.value) || 0 })}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="form-min-days"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t('leaves:statutoryTemplates.probationMonths')}</label>
                <input
                  type="number"
                  min={0}
                  value={form.probationMonths}
                  onChange={e => setForm({ ...form, probationMonths: parseInt(e.target.value, 10) || 0 })}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="form-probation-months"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="carryForward"
                checked={form.carryForwardAllowed}
                onChange={e => setForm({ ...form, carryForwardAllowed: e.target.checked, maxCarryForwardDays: e.target.checked ? form.maxCarryForwardDays : null })}
                className="h-4 w-4 text-primary rounded border-border focus-visible:ring-ring"
                data-testid="form-carry-forward"
              />
              <label htmlFor="carryForward" className="text-sm text-foreground">{t('leaves:statutoryTemplates.carryForwardAllowed')}</label>
              {form.carryForwardAllowed && (
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  value={form.maxCarryForwardDays ?? ''}
                  onChange={e => setForm({ ...form, maxCarryForwardDays: e.target.value ? parseFloat(e.target.value) : null })}
                  placeholder={t('leaves:statutoryTemplates.maxCarryDays')}
                  className="w-32 border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="form-max-carry-days"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t('leaves:statutoryTemplates.notes')}</label>
              <textarea
                value={form.notes || ''}
                onChange={e => setForm({ ...form, notes: e.target.value || null })}
                rows={2}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                data-testid="form-notes"
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button onClick={() => setEditModal(false)} variant="outline" data-testid="cancel-edit-btn">
              {t('common:Cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={actionLoading === 'save'}
              loading={actionLoading === 'save'}
              data-testid="save-template-btn"
            >
              {t('common:Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 end-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm ${toast.type === 'success' ? 'bg-success text-success-foreground' : 'bg-destructive text-destructive-foreground'}`} data-testid="toast">
          {toast.message}
        </div>
      )}
      </div>
    </PageShell>
  );
};

const mapStateToProps = (state: { user: { userId: string; company: { id: number }; role: string } }) => ({
  userId: state.user.userId,
  company: state.user.company,
  role: state.user.role,
});

export default connect(mapStateToProps)(StatutoryLeaveTemplates);
